import { useState, useRef } from 'react';
import { useCanvas } from './useCanvas';
import { useJobDebounce } from './useDebounce';
import useCanvasStore from '../stores/canvasStore';
import { useAgComponentUpdates } from './useAgComponentUpdates';
import { jobsAPI } from '../services/api';

/**
 * usePropertyHandlers
 *
 * Centralised hook that owns every handler related to changing element
 * properties (text, image, video, colour, font, sliders, section toggles)
 * and submitting the resulting render-job to the API.
 *
 * Used by both PropertiesPanel (full editor) and LitePropertiesPanel (lite view).
 *
 * handlePropertyChange accepts an optional `targetElement` so callers that
 * select an element and immediately fire a change don't have to wait for the
 * store to re-render (the closure's `selectedElement` would still be stale).
 */
const usePropertyHandlers = () => {
  const { selectedElement, updateElement, stopPlaybackLoop } = useCanvas();
  const saveHistory = useCanvasStore(state => state.saveHistory);
  const { handleAgComponentUpdate } = useAgComponentUpdates(stopPlaybackLoop);

  // State for controlling which sections are expanded – stored per element ID
  const [expandedSectionsByElement, setExpandedSectionsByElement] = useState({});
  // State to track if we're currently sliding
  const [isSliding, setIsSliding] = useState(false);

  // Ref to accumulate all field changes before sending
  const pendingFieldUpdates = useRef({});

  // ── helpers ──────────────────────────────────────────────────────────────

  /**
   * Collect all current field values from the given element so we always
   * send the complete state to the render API.
   */
  const collectAllFieldValues = (element) => {
    if (!element?.componentData?.fields) return {};

    const allFields = {
      AeId: element.componentData.id,
      componentId: element.id,
      // Include media crop/trim metadata if present
      ...(element.mediaCropTrim ? { mediaCropTrim: element.mediaCropTrim } : {})
    };

    element.componentData.fields.forEach((field) => {
      if (!field) return;

      const layerName = field.layerName;
      const propertyName = field.property;

      if (!layerName) return;

      const fieldKeys = Object.keys(field).filter(key =>
        !['layerName', 'property', 'label', 'type', 'width', 'height', 'duration', 'targetWidth', 'targetHeight'].includes(key)
      );

      fieldKeys.forEach((fieldKey) => {
        const value = field[fieldKey];

        if (value !== undefined && value !== null) {
          if (propertyName) {
            allFields[propertyName] = value;
          } else if (fieldKey.endsWith('_fontFamily')) {
            allFields[`${layerName}-font`] = value;
          } else {
            allFields[layerName] = value;
          }
        }
      });
    });

    return allFields;
  };

  // ── debounced job submission ─────────────────────────────────────────────
  // The element is captured at call-time via the accumulatedData._element key
  // so the debounced callback doesn't rely on the stale closure.

  const submitJobDebounced = useJobDebounce(async (accumulatedData) => {
    // Pull the element reference out of the payload (not sent to API)
    const { _element: element, ...apiPayload } = accumulatedData;
    if (!element) return;

    try {
      // Immediately set a "waiting" status to show rotating messages
      const waitingUpdates = {
        jobStatus: 'waiting',
        jobProgress: '0%',
      };
      updateElement(element.id, waitingUpdates, true);
      handleAgComponentUpdate(element, waitingUpdates);

      await jobsAPI.submitJob(apiPayload);

      // Clear the accumulated updates after successful submission
      pendingFieldUpdates.current = {};

      // Get latest job status and update element state
      const eventSource = jobsAPI.getUpdate(apiPayload.componentId);

      eventSource.onmessage = (event) => {
        const data = JSON.parse(event.data);
        console.log('Job status:', data.status, data.progress);

        const updates = {
          jobStatus: data.status,
          jobProgress: data.progress,
        };

        updateElement(element.id, updates, true);
        handleAgComponentUpdate(element, updates);
      };

      eventSource.onerror = (error) => {
        console.error('SSE error:', error);
        eventSource.close();

        const errorUpdates = { jobStatus: 'error', jobProgress: '0%' };
        updateElement(element.id, errorUpdates, true);
        handleAgComponentUpdate(element, errorUpdates);
      };
    } catch (error) {
      console.error('Failed to submit field update to API:', error);

      const errorUpdates = { jobStatus: 'error', jobProgress: '0%' };
      updateElement(element.id, errorUpdates, true);
      handleAgComponentUpdate(element, errorUpdates);
    }
  }, 1000);

  // ── main property-change handler ─────────────────────────────────────────

  /**
   * @param {string}  property    – property path, e.g. 'componentData.fields[0].textfield'
   * @param {*}       value       – new value
   * @param {boolean} skipHistory – skip undo history entry
   * @param {object}  [targetElement] – optional element override; when provided
   *   the handler uses this element instead of the hook's `selectedElement`.
   *   This avoids the stale-closure problem when `selectElement()` and
   *   `handlePropertyChange()` are called in the same event handler.
   */
  const handlePropertyChange = async (property, value, skipHistory = false, targetElement = null) => {
    const element = targetElement || selectedElement;
    if (!element) return;

    let updates = {};
    let shouldAccumulateField = false;

    // Check if property contains array notation like 'componentData.fields[0].textfield'
    if (property.includes('[')) {
      const regex = /([^\[\]\.]+)|\[(\d+)\]/g;
      const parts = [];
      let match;

      while ((match = regex.exec(property)) !== null) {
        if (match[1]) {
          parts.push(match[1]);
        } else if (match[2]) {
          parts.push(parseInt(match[2]));
        }
      }

      // Check if this is a componentData.fields update
      if (
        parts[0] === 'componentData' &&
        parts[1] === 'fields' &&
        typeof parts[2] === 'number'
      ) {
        const fieldIndex = parts[2];
        const fieldName = parts[3];

        if (element.componentData?.id && fieldName) {
          const layerName =
            element.componentData?.fields?.[fieldIndex]?.layerName;
          const propertyName =
            element.componentData?.fields?.[fieldIndex]?.property;

          if (!layerName) {
            console.error('Layer name not found for field index:', fieldIndex);
          } else {
            shouldAccumulateField = true;

            // Collect ALL current field values
            pendingFieldUpdates.current = collectAllFieldValues(element);

            // Update the specific field that just changed
            if (propertyName) {
              pendingFieldUpdates.current[propertyName] = value;
            } else if (fieldName.endsWith('_fontFamily')) {
              pendingFieldUpdates.current[`${layerName}-font`] = value;
            } else {
              pendingFieldUpdates.current[layerName] = value;
            }
          }
        }
      }

      // Build the nested update object
      let current = updates;
      let elementCurrent = element;

      for (let i = 0; i < parts.length - 1; i++) {
        const part = parts[i];

        if (typeof parts[i + 1] === 'number') {
          current[part] = elementCurrent?.[part]
            ? [...elementCurrent[part]]
            : [];
          elementCurrent = elementCurrent?.[part];
          current = current[part];
        } else if (typeof part === 'number') {
          if (!current[part]) {
            current[part] = elementCurrent?.[part]
              ? { ...elementCurrent[part] }
              : {};
          }
          elementCurrent = elementCurrent?.[part];
          current = current[part];
        } else {
          if (!current[part]) {
            current[part] = {};
          }
          elementCurrent = elementCurrent?.[part];
          current = current[part];
        }
      }

      current[parts[parts.length - 1]] = value;
    } else if (property.includes('.')) {
      const keys = property.split('.');
      let current = updates;

      for (let i = 0; i < keys.length - 1; i++) {
        current[keys[i]] = {};
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
    } else {
      updates[property] = value;
    }

    // Deep merge + PIXI sync
    updateElement(element.id, updates, skipHistory || isSliding);

    // Submit accumulated field data to API
    if (
      shouldAccumulateField &&
      Object.keys(pendingFieldUpdates.current).length > 2
    ) {
      // Attach the element reference so the debounced callback can use it
      submitJobDebounced({ ...pendingFieldUpdates.current, _element: element });
    }
  };

  // ── slider helpers ───────────────────────────────────────────────────────

  const handleSliderStart = () => {
    setIsSliding(true);
  };

  const handleSliderEnd = () => {
    setIsSliding(false);
    saveHistory();
  };

  // ── section toggle helpers ───────────────────────────────────────────────

  const handleToggleSection = (section) => {
    if (!selectedElement) return;

    setExpandedSectionsByElement((prev) => ({
      ...prev,
      [selectedElement.id]: {
        ...prev[selectedElement.id],
        [section]: !prev[selectedElement.id]?.[section],
      },
    }));
  };

  const getCurrentExpandedSections = () => {
    if (!selectedElement)
      return { text: false, color: false, image: false, video: false, audio: false };

    return {
      text: expandedSectionsByElement[selectedElement.id]?.text || false,
      color: expandedSectionsByElement[selectedElement.id]?.color || false,
      image: expandedSectionsByElement[selectedElement.id]?.image || false,
      video: expandedSectionsByElement[selectedElement.id]?.video || false,
      audio: expandedSectionsByElement[selectedElement.id]?.audio || false,
    };
  };

  return {
    selectedElement,
    handlePropertyChange,
    handleSliderStart,
    handleSliderEnd,
    handleToggleSection,
    getCurrentExpandedSections,
    isSliding,
  };
};

export default usePropertyHandlers;
